<template>
  <div class="scaling-dashboard">
    <!-- 1) Scaling experiments table -->
    <div class="experiments">
      <h3>Scaling Experiments</h3>
      <table>
        <thead>
          <tr>
            <th>Scaling ID</th>
            <th>Started On</th>
            <th>Status</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="exp in scalingExperiments" :key="exp.ScalingId">
            <td>{{ exp.ScalingId }}</td>
            <td>{{ exp.StartedOn }}</td>
            <td>{{ exp.Status }}</td>
            <td>
              <button @click="selectExperiment(exp.ScalingId)">
                View Summaries
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- 2) Charts -->
    <div class="charts">
      <!-- loss vs num_samples -->
      <div v-if="selectedScalingId" class="chart-container">
        <h2>Scaling {{ selectedScalingId }}: MAE vs Samples</h2>
        <v-chart :option="maeChartOptions" style="height: 400px; width: 100%" />
      </div>
      <!-- F1 vs num_samples -->
      <div v-if="selectedScalingId" class="chart-container">
        <h2>Scaling {{ selectedScalingId }}: F1 vs Samples</h2>
        <v-chart :option="f1ChartOptions" style="height: 400px; width: 100%" />
      </div>
    </div>

    <!-- 3) Start new scaling modal -->
    <button class="new-btn" @click="openModal">+ New Scaling Experiment</button>
    <div v-if="modalVisible" class="modal-overlay">
      <div class="modal">
        <h2>Start Scaling Experiment</h2>
        <form
          @submit.prevent="currentStep === 1 ? goToStep2() : startScaling()"
        >
          <!-- Step 1: Hyperparameters -->
          <div v-if="currentStep === 1" class="hyperparams">
            <div
              v-for="(schema, key) in hyperparamsSchema.properties"
              :key="key"
              class="form-item"
            >
              <label :for="`hp-${key}`">{{ key }}</label>
              <!-- Number / Integer -->
              <input
                v-if="['number', 'integer'].includes(schema.type)"
                type="number"
                :step="schema.type === 'integer' ? 1 : 'any'"
                :min="schema.minimum"
                :max="schema.maximum"
                :id="`hp-${key}`"
                v-model.number="form.hyperparams[key]"
                :required="hyperparamsSchema.required.includes(key)"
              />
              <!-- Enum -->
              <select
                v-else-if="schema.enum"
                :id="`hp-${key}`"
                v-model="form.hyperparams[key]"
                :required="hyperparamsSchema.required.includes(key)"
              >
                <option v-for="opt in schema.enum" :key="opt" :value="opt">
                  {{ opt }}
                </option>
              </select>
              <!-- String -->
              <input
                v-else-if="schema.type === 'string'"
                type="text"
                :id="`hp-${key}`"
                v-model="form.hyperparams[key]"
                :required="hyperparamsSchema.required.includes(key)"
              />
            </div>
          </div>

          <!-- Step 2: Dataset, models, fractions -->
          <div v-if="currentStep === 2">
            <!-- Dataset -->
            <div class="form-item">
              <label for="dataset-select">Dataset:</label>
              <select id="dataset-select" v-model="form.dataset_id" required>
                <option
                  v-for="ds in datasets"
                  :key="ds.DatasetId"
                  :value="ds.DatasetId"
                >
                  {{ ds.DatasetId }} — {{ ds.CreatedOn }}
                </option>
              </select>
            </div>
            <!-- Models -->
            <div class="form-item">
              <label>Models:</label>
              <div
                v-for="(m, idx) in form.models"
                :key="idx"
                class="inline-group"
              >
                <select v-model="form.models[idx]" required>
                  <option disabled value="">Select model</option>
                  <option
                    v-for="opt in availableModels"
                    :key="opt"
                    :value="opt"
                  >
                    {{ opt }}
                  </option>
                </select>
                <button
                  type="button"
                  @click="removeModel(idx)"
                  v-if="form.models.length > 1"
                >
                  −
                </button>
                <button
                  type="button"
                  @click="addModel"
                  v-if="idx === form.models.length - 1"
                >
                  ＋
                </button>
              </div>
            </div>
            <!-- Fractions -->
            <div class="form-item">
              <label>Data Fractions:</label>
              <div
                v-for="(f, idx) in form.data_fractions"
                :key="idx"
                class="inline-group"
              >
                <input
                  type="number"
                  v-model.number="form.data_fractions[idx]"
                  step="0.01"
                  min="0"
                  max="1"
                  required
                />
                <button
                  type="button"
                  @click="removeFraction(idx)"
                  v-if="form.data_fractions.length > 1"
                >
                  −
                </button>
                <button
                  type="button"
                  @click="addFraction"
                  v-if="idx === form.data_fractions.length - 1"
                >
                  ＋
                </button>
              </div>
            </div>
          </div>

          <div class="actions">
            <button type="submit" :disabled="starting">
              {{
                starting
                  ? currentStep === 2
                    ? "Starting…"
                    : "Loading…"
                  : currentStep === 1
                  ? "Next"
                  : "Start Experiment"
              }}
            </button>
            <button
              type="button"
              @click="currentStep === 1 ? closeModal() : goToStep1()"
            >
              {{ currentStep === 1 ? "Cancel" : "Back" }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { api_config } from "@/env.ts";
import axios from "axios";
import { getToken } from "@/keycloak-wrapper.ts";
import { use } from "echarts/core";
import VChart from "vue-echarts";
import { CanvasRenderer } from "echarts/renderers";
import { ScatterChart } from "echarts/charts";
import {
  LegendComponent,
  TooltipComponent,
  GridComponent,
  TitleComponent,
  DatasetComponent,
} from "echarts/components";

use([
  CanvasRenderer,
  ScatterChart,
  LegendComponent,
  TooltipComponent,
  GridComponent,
  TitleComponent,
  DatasetComponent,
]);

export default {
  name: "ScalingDashboard",
  components: { VChart },
  data() {
    return {
      token: null,
      datasets: [],
      availableModels: [],
      scalingExperiments: [],
      metrics: [],
      selectedScalingId: null,
      scalingSummaries: null,

      modalVisible: false,
      starting: false,
      currentStep: 1,
      hyperparamsSchema: { properties: {}, required: [] },
      form: {
        hyperparams: {},
        dataset_id: null,
        models: [""],
        data_fractions: [1.0],
      },

      maeChartOptions: {},
      f1ChartOptions: {},
      symbolMap: {},
    };
  },
  async mounted() {
    this.token = await getToken();
    await this.fetchDatasets();
    await this.fetchAvailableModels();
    await this.fetchScalingExperiments();
  },
  methods: {
    authHeaders() {
      return { Authorization: `Bearer ${this.token}` };
    },
    openModal() {
      this.modalVisible = true;
      this.currentStep = 1;
      this.resetForm();
      this.fetchHyperparamsSchema();
    },
    closeModal() {
      this.modalVisible = false;
      this.resetForm();
    },
    goToStep2() {
      this.currentStep = 2;
    },
    goToStep1() {
      this.currentStep = 1;
    },
    resetForm() {
      this.form.hyperparams = {};
      if (this.datasets.length)
        this.form.dataset_id = this.datasets[0].DatasetId;
      this.form.models = [""];
      this.form.data_fractions = [1.0];
    },
    async fetchDatasets() {
      const { data } = await axios.get(`${api_config.ml_backend}/datasets`, {
        headers: this.authHeaders(),
      });
      this.datasets = data.datasets;
      if (this.datasets.length)
        this.form.dataset_id =
          this.datasets[this.datasets.length - 1].DatasetId;
    },
    async fetchAvailableModels() {
      const { data } = await axios.get(
        `${api_config.ml_backend}/scaling/models`,
        { headers: this.authHeaders() }
      );
      this.availableModels = data.models;
    },
    async fetchScalingExperiments() {
      const { data } = await axios.get(
        `${api_config.ml_backend}/scaling/experiments`,
        { headers: this.authHeaders() }
      );
      this.scalingExperiments = data.scaling_experiments;
    },
    async fetchHyperparamsSchema() {
      const { data } = await axios.get(
        `${api_config.ml_backend}/scaling/params`,
        { headers: this.authHeaders() }
      );
      this.hyperparamsSchema = {
        ...data,
        required: Array.isArray(data.required) ? data.required : [],
      };
      // Initialize hyperparams using schema defaults when available
      Object.entries(this.hyperparamsSchema.properties).forEach(
        ([key, schema]) => {
          let value;
          if (schema.default !== undefined) {
            value = schema.default;
          } else if (schema.type === "integer") {
            value = 0;
          } else if (schema.type === "number") {
            value = 0.0;
          } else if (schema.enum) {
            value = schema.enum[0];
          } else {
            value = "";
          }
          // assign to reactive object
          this.form.hyperparams[key] = value;
        }
      );
    },
    async fetchScalingSummaries() {
      if (!this.selectedScalingId) return;
      const { data } = await axios.get(
        `${api_config.ml_backend}/scaling/${this.selectedScalingId}/summaries`,
        { headers: this.authHeaders() }
      );
      this.scalingSummaries = data.summaries;
      this.updateChart();
    },
    selectExperiment(id) {
      this.selectedScalingId = id;
      this.fetchScalingSummaries();
    },
    addModel() {
      this.form.models.push("");
    },
    removeModel(i) {
      this.form.models.splice(i, 1);
    },
    addFraction() {
      this.form.data_fractions.push(1.0);
    },
    removeFraction(i) {
      this.form.data_fractions.splice(i, 1);
    },
    async startScaling() {
      this.starting = true;
      try {
        const payload = {
          models: this.form.models.filter((m) => m),
          data_fractions: this.form.data_fractions,
          hyperparams: this.form.hyperparams,
          dataset_id: this.form.dataset_id,
        };
        await axios.post(`${api_config.ml_backend}/scaling/start`, payload, {
          headers: this.authHeaders(),
        });
        await this.fetchScalingExperiments();
        this.closeModal();
      } catch (e) {
        console.error(e);
        alert("Failed to start scaling experiment");
      } finally {
        this.starting = false;
      }
    },
    async viewMetrics(scalingId) {
      this.selectedScalingId = scalingId;
      const { data } = await axios.get(
        `${api_config.ml_backend}/scaling/${scalingId}/metrics`,
        { headers: this.authHeaders() }
      );
      this.metrics = data.metrics;
    },
    updateChart() {
      // group by model_name
      const n_samples_vs_mae = {};
      const n_samples_vs_f1 = {};
      this.scalingSummaries.forEach((r) => {
        if (!n_samples_vs_mae[r.model_name]) {
          n_samples_vs_mae[r.model_name] = [];
          n_samples_vs_f1[r.model_name] = [];
        }
        n_samples_vs_mae[r.model_name].push([r.num_samples, r.MAE]);
        n_samples_vs_f1[r.model_name].push([r.num_samples, r.F1]);
      });
      // assign symbols dynamically
      const models = Object.keys(n_samples_vs_mae);
      models.forEach((m, i) => {
        this.symbolMap[m] = [
          "circle",
          "rect",
          "triangle",
          "diamond",
          "pin",
          "arrow",
        ][i % 6];
      });
      const mae_series = models.map((m) => ({
        name: m,
        type: "scatter",
        symbol: this.symbolMap[m],
        data: n_samples_vs_mae[m],
      }));
      const f1_series = models.map((m) => ({
        name: m,
        type: "scatter",
        symbol: this.symbolMap[m],
        data: n_samples_vs_f1[m],
      }));
      this.maeChartOptions = {
        tooltip: {
          trigger: "item",
          formatter: ({ seriesName, value }) =>
            `Model: ${seriesName}<br/>Samples: ${
              value[0]
            }<br/>MAE: ${value[1].toFixed(4)}`,
        },
        legend: { data: models },
        xAxis: { name: "Num Samples" },
        yAxis: { name: "MAE" },
        series: mae_series,
      };
      this.f1ChartOptions = {
        tooltip: {
          trigger: "item",
          formatter: ({ seriesName, value }) =>
            `Model: ${seriesName}<br/>Samples: ${
              value[0]
            }<br/>F1: ${value[1].toFixed(4)}`,
        },
        legend: { data: models },
        xAxis: { name: "Num Samples" },
        yAxis: { name: "F1" },
        series: f1_series,
      };
    },
  },
};
</script>

<style scoped>
.scaling-dashboard {
  width: 100%;
  height: 100%;
  display: grid;
  grid-template-rows: 18.75rem auto 2.5rem;
}
.new-btn {
  margin-bottom: 1rem;
  height: var(--control-height-lg);
  margin: 0;
  padding: var(--spacing-sm) 0.0625rem;
  border-radius: var(--radius-md);
  cursor: pointer;
}
.charts {
  height: 100%;
  background-color: rgb(249, 249, 249);
  border-radius: var(--radius-md);
  display: grid;
  grid-template-columns: 1fr 1fr;
}
.chart-container {
  display: grid;
  grid-template-rows: var(--control-height-sm);
}
.experiments {
  height: 100%;
}
.experiments table,
.metrics table {
  width: 100%;
  border-collapse: collapse;
}
table tbody {
  overflow-y: scroll;
}
table th,
table td {
  border: 1px solid var(--colour-border);
  padding: 2px 0.5rem;
  text-align: left;
  font-size: 1rem;
}
table button {
  padding: 2px 0.5rem;
  font-size: 1rem;
  cursor: pointer;
}
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
}
.modal {
  background: white;
  padding: 1rem;
  border-radius: var(--radius-sm);
  width: 25rem;
}
.hyperparams {
  width: 100%;
}
.form-item {
  margin-bottom: 0.75rem;
  display: grid;
  grid-template-columns: 1fr 1fr;
}
.inline-group {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}
.inline-group select,
.inline-group input {
  flex: 1;
}
.actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
}
</style>
